import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@attendance/ui';
import { useOptionalCopy } from './useOptionalCopy.js';
import { FIELD_BY_ID, type SettingField } from './settingsCatalog.js';
import { sameValue } from './settingsPatch.js';
import {
  ChangeLine,
  NotInForceTag,
  PayTag,
  SettingControl,
  formatSettingValue,
} from './SettingsControls.js';

// ---------------------------------------------------------------------------
// One setting: name, what it does, what it is now, and — the moment it is
// edited — what it is about to become and what that costs.
//
// Extracted from SettingsPage.tsx, which is over its line budget and could not
// grow to hold the two fixes below.
// ---------------------------------------------------------------------------

// Memoised because a group holds up to 34 of these: without it, one keystroke
// in the search box (or one toggle) re-renders every row on screen. `onChange`
// takes the field id so the parent can pass one stable callback.
export const FieldRow = memo(function FieldRow({
  field,
  value,
  before,
  disabled,
  notInForce,
  onChange,
}: {
  field: SettingField;
  value: unknown;
  before: unknown;
  disabled: boolean;
  /** The engine that would read this value is not running. See the group alert. */
  notInForce?: boolean;
  onChange: (id: string, next: unknown) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const optional = useOptionalCopy();
  const inputId = `setting-${field.id.replace(/\./g, '-')}`;
  const label = t(`settings.f.${field.id}.label`);
  const hint = optional(`settings.f.${field.id}.hint`);
  const impact = optional(`settings.f.${field.id}.impact`);
  const changed = !sameValue(value, before);
  const wide =
    field.kind === 'textarea' ||
    field.kind === 'weekdays' ||
    field.kind === 'schedules';

  /*
   * A BLOCKED CONTROL NAMES ITS OBSTACLE (§3.11).
   *
   * Twenty-six of the catalogue's fields carry `enabledBy`, and when the gate
   * is off the control was greyed with nothing on the row to say why: the
   * four Ramadan rules, the four summer-hours rules, the three overtime rates.
   * The reader's only route to the answer was to scroll up, find the switch
   * the greying came from, and infer. That is the "dead grey control wearing
   * the happy-path label" §3.11 exists to ban, and it is worse in a card of
   * eight rows where three are dead and five are live.
   *
   * The sentence names the SWITCH, by the label it actually carries in this
   * group, so the reader's next move is one they can make on this screen.
   */
  const gateLabel =
    disabled && field.enabledBy && FIELD_BY_ID.has(field.enabledBy)
      ? t(`settings.f.${field.enabledBy}.label`)
      : '';

  return (
    /*
     * `flex-wrap` IS LOAD-BEARING, not tidiness.
     *
     * This row lays out three children: the label column (`flex-1`, and
     * `min-w-0` so a long hint can shrink), the control (a fixed 14–16rem), and
     * — only once the value differs — the change line, which asks for
     * `basis-full` because it belongs on a line of its own underneath.
     *
     * Without wrapping, `flex-basis: 100%` is a demand for the WHOLE row width
     * on the SAME line, and the only child that can yield is the one told it may
     * shrink to zero. The label column collapsed to about 60px and its hint
     * wrapped one word per line, on every field an admin had touched — i.e. the
     * defect only appeared after you edited something, which is why it survived.
     *
     * `gap-x-section` rather than `gap-section`: the change line carries its own `mt-2`, and
     * a 1.5rem row gap on top of that would open a hole between a field and the
     * sentence explaining what changing it does.
     */
    <div className={`py-3 ${wide ? '' : 'sm:flex sm:flex-wrap sm:items-start sm:gap-x-section'}`}>
      <div className={wide ? '' : 'min-w-0 sm:flex-1'}>
        <div className="flex flex-wrap items-center gap-element">
          {/* A FIELD LABEL, and this is the one place the toolbar's
              "no stacked label+control pairs" rule does not apply — a form keeps
              its labels. `text-label` is the 12.5px/600 sentence-case step the
              rules ask for, never 11px and never uppercase.

              NOT dimmed when the row is dead. `Label` decided that for every
              call site and wrote down why (see its own note): a faded label
              read as more available than the disabled control beside it. The
              obstacle is stated in words below instead, which is what §3.11
              asks for anyway. */}
          <Label htmlFor={inputId}>{label}</Label>
          {field.pay && <PayTag />}
          {notInForce && <NotInForceTag />}
        </div>
        {hint && (
          <p className="mt-1 max-w-prose text-copy-sm leading-relaxed text-muted-foreground">
            {hint}
          </p>
        )}
        {gateLabel && (
          <p className="mt-1 text-copy-sm text-foreground-tertiary">
            {t('settings.needsSwitch', { label: gateLabel })}
          </p>
        )}
      </div>
      {/*
       * ONE CONTROL RAIL PER CARD.
       *
       * The column is a fixed 14–16rem and always was; what it did not have was
       * an alignment. A `Select` filled it, a number box was `w-28` at its
       * LEADING edge, and a toggle justified itself to the TRAILING one — so a
       * card like "What counts as a day" (three numbers, two switches, a time,
       * a select) drew its eight controls down three different rails, and the
       * eye had to find each one. `justify-end` puts every control's trailing
       * edge on the card's, which is the same rail the section's own frame
       * already draws.
       *
       * Only from `sm` up: below it the row is stacked, the label is at the
       * start of the line, and a control shoved to the far edge of a 390px
       * screen reads as belonging to nothing.
       */}
      <div
        className={
          wide ? 'mt-2' : 'mt-2 flex shrink-0 justify-start sm:mt-0 sm:w-56 sm:justify-end md:w-64'
        }
      >
        <SettingControl
          field={field}
          inputId={inputId}
          value={value}
          disabled={disabled}
          label={label}
          onChange={(next) => onChange(field.id, next)}
        />
      </div>
      {changed && (
        <div className={wide ? '' : 'sm:basis-full'}>
          <ChangeLine
            before={formatSettingValue(field, before, t)}
            after={formatSettingValue(field, value, t)}
            impact={impact || undefined}
            pay={field.pay}
          />
        </div>
      )}
    </div>
  );
});
